import { useField, useFormikContext } from 'formik';
import { useMemo, useState, useEffect } from 'react';

import { getIsERHospital } from 'utils/helpers';
import { useGetQuery } from 'store/api/acceptance';
import { useDetailsQuery } from 'store/api/hospitals';
import { CorporateGroupType, CallOutcomeEmergency } from 'types';
import { useOnCallProtocolQuery } from 'store/api/on-call-protocol';
import { getCorporateGroupOptions } from 'components/cases/helpers';
import { useHospitalTime } from 'components/cases/form/useHospitalTime';
import { useListQuery as useCorporateGroupListQuery } from 'store/api/corporate-group';
import {
  useFormEntities,
  callOutcomeModel,
  CaseFormTextOutProps,
} from 'components/cases/form';

export const useReferToOtherEr = ({
  setReferral,
}: Pick<CaseFormTextOutProps, 'setReferral'>) => {
  const { values } = useFormikContext<any>();
  const [showReferrals, setShowReferrals] = useState(false);
  const { hospital, referrals, hospitalId, isMainHospital } = useFormEntities();
  const { isHospitalOpen } = useHospitalTime();
  const {
    emergency: { referToEr, referToAnotherER },
  } = callOutcomeModel;

  const activeFieldPath = useMemo(() => {
    const emergencyType = values?.callOutcome?.emergency?.type;
    const isReferToAnotherER =
      emergencyType === CallOutcomeEmergency.ReferToAnotherER;
    const referToAnotherERValue = values?.[referToAnotherER?.hospitalRefId];

    if (isReferToAnotherER) {
      return referToAnotherER;
    }

    if (
      referToAnotherERValue !== undefined &&
      referToAnotherERValue !== null &&
      referToAnotherERValue !== ''
    ) {
      return referToAnotherER;
    }

    return referToEr;
  }, [values, referToAnotherER, referToEr]);

  const isERHospital = getIsERHospital(hospital?.speciality);
  const isOpenedERHospital = isHospitalOpen && isERHospital;

  const { data: corporateGroup, ...props } = useCorporateGroupListQuery(
    { hospitalId: hospitalId!, type: CorporateGroupType.All },
    { skip: !hospitalId || !isMainHospital }
  );

  const { referToEr: corporateOptions } = useMemo(
    () => getCorporateGroupOptions(corporateGroup?.grouped) ?? {},
    [corporateGroup?.grouped]
  );

  const [corporate, , corporateHelpers] = useField(
    activeFieldPath.corporateHospitalId
  );
  const [referral, , referralHelpers] = useField(activeFieldPath.hospitalRefId);
  const [, , referralMainHospitalHelpers] = useField(
    activeFieldPath.hospitalId
  );

  const { data: selectedCorporateHospital } = useDetailsQuery(
    corporate.value!,
    {
      skip: !corporate.value,
    }
  );

  const isShowMainERHospital = isOpenedERHospital && !showReferrals;
  const acceptanceHospitalId = isShowMainERHospital
    ? hospitalId
    : corporate.value || referral.value;

  const { data: acceptance } = useGetQuery(
    { hospitalId: String(acceptanceHospitalId) },
    { skip: !acceptanceHospitalId }
  );

  useEffect(() => {
    if (isOpenedERHospital) {
      if (isShowMainERHospital) {
        referralHelpers.setValue(null);
        corporateHelpers.setValue(null);
        referralMainHospitalHelpers.setValue(hospitalId);
      } else {
        corporateHelpers.setValue(null);
        referralHelpers.setValue(hospitalId);
        referralMainHospitalHelpers.setValue(null);
      }
    } else {
      setShowReferrals(true);
    }
  }, [isOpenedERHospital, isShowMainERHospital, hospitalId]);

  const hospitalsOptions = useMemo(
    () =>
      referrals?.map((item) => {
        const id = String(item.id);

        return {
          id,
          value: id,
          label: item.name ?? id,
        };
      }) ?? [],
    [referrals]
  );

  useEffect(() => {
    if (!corporate.value && corporateOptions?.length) {
      corporateHelpers.setValue(String(corporateOptions[0]?.data?.id));
      return;
    }

    if (!referral.value) {
      if (isShowMainERHospital) {
        referralHelpers.setValue(null);
        referralMainHospitalHelpers.setValue(hospitalId);
        return;
      }

      if (hospitalsOptions?.length) {
        referralMainHospitalHelpers.setValue(null);
        referralHelpers.setValue(hospitalsOptions[0].id);
      }
    }
  }, [
    referral.value,
    corporate.value,
    corporateOptions,
    hospitalsOptions,
    isOpenedERHospital,
    isShowMainERHospital,
  ]);

  const referralData = useMemo(() => {
    if (corporate.value) {
      return {
        acceptance,
        hospital: selectedCorporateHospital,
      };
    } else if (referral.value) {
      const data = referrals?.find(({ id }) => id === Number(referral.value));

      return {
        acceptance,
        hospital: data,
      };
    }

    return undefined;
  }, [
    referrals,
    acceptance,
    referral.value,
    corporate.value,
    selectedCorporateHospital,
    acceptanceHospitalId,
  ]);

  const { data: protocol } = useOnCallProtocolQuery(String(hospitalId), {
    skip: !!referralData?.hospital || !hospitalId,
  });

  const payload = useMemo(() => {
    return {
      protocol,
      acceptance,
      setReferral,
      isERHospital,
      referralData,
      showReferrals,
      setShowReferrals,
      data: referralData?.hospital,
      mainHospital: selectedCorporateHospital ?? hospital,
      referToERProps: {
        referToEr,
        isMainHospital,
        hospitalsOptions,
        options: corporateOptions,
        hospitalName: hospital?.name,
      },
    };
  }, [
    hospital,
    protocol,
    referToEr,
    acceptance,
    setReferral,
    referralData,
    isERHospital,
    showReferrals,
    isMainHospital,
    setShowReferrals,
    corporateOptions,
    hospitalsOptions,
    referralData?.hospital,
    selectedCorporateHospital,
  ]);

  return { ...payload, requestProps: props };
};
